import { ShirtspaceResponse } from "@services/shirtspace.types";
import { Product, ProductAvailability } from "~/entities/product/types";
import {
  isColorVariantTypesafe,
  isInventoryVariantTypesafe,
  isPriceVariantTypesafe,
  isVariantTypesafe,
} from "~/entities/variant/validators";

export function isProductAvailabilityResponseTypesafe(
  productAvailabilityResponse: unknown,
): ShirtspaceResponse<ProductAvailability> | null {
  try {
    if (
      productAvailabilityResponse === null ||
      typeof productAvailabilityResponse !== "object"
    ) {
      throw new Error("ProductAvailabilityResponse must be a non-null object");
    }

    const par = productAvailabilityResponse as Record<string, unknown>;

    if (typeof par["data"] !== "object") {
      throw new Error(
        "ProductAvailabilityResponse must have a 'data' property",
      );
    }

    if (!isProductAvailabilityTypesafe(par.data)) {
      throw new Error(
        "ProductAvailabilityResponse 'data' must be a valid ProductAvailability",
      );
    }

    return par as ShirtspaceResponse<ProductAvailability>;
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error("An unknown error occurred");
    }

    return null;
  }
}

export function isProductAvailabilityTypesafe(
  productAvailability: unknown,
): ProductAvailability | null {
  try {
    if (!productAvailability || typeof productAvailability !== "object") {
      throw new Error("ProductAvailability must be a non-null object");
    }

    const pa = productAvailability as Record<string, unknown>;

    // Check required primitive properties
    if (
      typeof pa.fullProductTotal !== "number" ||
      typeof pa.selectedIndex !== "number" ||
      typeof pa.bulkRequiresSignIn !== "boolean"
    ) {
      throw new Error(
        "ProductAvailability must have valid fullProductTotal, selectedIndex, and bulkRequiresSignIn",
      );
    }

    // Check bulk array
    if (!Array.isArray(pa.bulk)) {
      throw new Error("ProductAvailability must have a bulk array");
    }

    // Check inventories array
    if (!Array.isArray(pa.inventories)) {
      throw new Error("ProductAvailability must have an inventories array");
    }

    // Check each inventory variant in the array
    for (const inventory of pa.inventories) {
      if (!isInventoryVariantTypesafe(inventory)) {
        throw new Error("All inventory variants must be valid variants");
      }
    }

    // Check prices array
    if (!Array.isArray(pa.prices)) {
      throw new Error("ProductAvailability must have a prices array");
    }

    // Check each price variant in the array
    for (const price of pa.prices) {
      if (!isPriceVariantTypesafe(price)) {
        throw new Error("All price variants must be valid variants");
      }
    }

    // If all checks pass, return the product availability
    return productAvailability as ProductAvailability;
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error("An unknown error occurred");
    }

    return null;
  }
}

export function isProductTypesafe(product: unknown): Product | null {
  try {
    if (!product || typeof product !== "object") {
      throw new Error("Product must be a non-null object");
    }

    const p = product as Record<string, unknown>;

    // Check required primitive properties
    if (
      typeof p.id !== "string" ||
      typeof p.name !== "string" ||
      typeof p.styleCode !== "string" ||
      typeof p.slug !== "string"
    ) {
      throw new Error(
        "Product must have valid id, name, styleCode, and slug strings",
      );
    }

    // Check variants array
    if (!Array.isArray(p.variants)) {
      throw new Error("Product must have a variants array");
    }

    // Check each variant in the array
    for (const variant of p.variants) {
      if (!isVariantTypesafe(variant)) {
        throw new Error("All product variants must be valid variants");
      }
    }

    // Check defaultVariant
    if (!p.defaultVariant || !isVariantTypesafe(p.defaultVariant)) {
      throw new Error("Product must have a valid defaultVariant");
    }

    // Check colors array
    if (!Array.isArray(p.colors)) {
      throw new Error("Product must have a colors array");
    }

    // Check each color variant in the array
    for (const color of p.colors) {
      if (!isColorVariantTypesafe(color)) {
        throw new Error("All product colors must be valid color variants");
      }
    }

    // Check meta object
    if (!p.meta || typeof p.meta !== "object") {
      throw new Error("Product must have a meta object");
    }
    const meta = p.meta as Record<string, unknown>;
    if (
      typeof meta.title !== "string" ||
      typeof meta.description !== "string"
    ) {
      throw new Error(
        "Product meta must have valid title and description strings",
      );
    }

    // If all checks pass, return the product
    return product as Product;
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error("An unknown error occurred");
    }

    return null;
  }
}
